// ── Calendar Overlap Layout ──
// Apple Calendar-style overlap cascade: overlapping blocks indent slightly
// and layer, with the later-starting block on top. POI and note blocks
// participate identically.

import { epochMs } from "./zonedTime";
import type { ZonedTimeString } from "@poi-planner/shared";

/** Left indent per overlap depth, in percent of the column width. */
export const CASCADE_INDENT_PCT = 6;

/** Base z-index for blocks (markers sit below, chips above). */
export const BLOCK_BASE_Z = 2;

export interface LayoutInput {
  id: string;
  startTime: ZonedTimeString;
  endTime: ZonedTimeString;
}

export interface BlockLayout {
  /** Overlap depth: number of earlier blocks still covering this block's start */
  depth: number;
  /** Left offset as percent of column width */
  leftPct: number;
  /** Remaining width as percent of column width */
  widthPct: number;
  zIndex: number;
}

/**
 * Computes the cascade layout for one day's blocks.
 *
 * Sort by start instant (tiebreak: longer block first); a block's depth is
 * the count of earlier blocks in that order whose interval still covers this
 * block's start. Depth drives a subtle left indent and the stacking order —
 * the later block layers on top.
 */
export function computeOverlapLayout(
  blocks: LayoutInput[],
): Map<string, BlockLayout> {
  const items = blocks
    .map((block) => ({
      id: block.id,
      start: epochMs(block.startTime),
      end: epochMs(block.endTime),
    }))
    .sort((a, b) => a.start - b.start || b.end - a.end);

  const layouts = new Map<string, BlockLayout>();

  for (let i = 0; i < items.length; i++) {
    const block = items[i];
    let depth = 0;
    for (let j = 0; j < i; j++) {
      const earlier = items[j];
      if (earlier.start <= block.start && block.start < earlier.end) {
        depth++;
      }
    }
    const leftPct = depth * CASCADE_INDENT_PCT;
    layouts.set(block.id, {
      depth,
      leftPct,
      widthPct: 100 - leftPct,
      zIndex: BLOCK_BASE_Z + depth,
    });
  }

  return layouts;
}
